{max} := Math

{ Container, Sprite } from "pixi.js"

{ clamp } from @danielx/lib

type { Tool } from ./tools.civet
{ noteName, quantize } from ./lib/util.civet

// The pointer/tool wiring both rolls share: tools work in the note field
// exactly as on the staff (right click erases, performing modes are
// play-only), and pressing the drawn keyboard previews the note on the
// active channel. The view supplies its geometry — screen→(beat, note) and
// the keyboard hit test — everything else is policy that must not drift
// between the sibling views.
export rollInteraction := (opts: {
  player: Player
  container: Container
  deferCleanup: (() =>)[]
  screenPositionToBeatNote: (x: number, y: number, event: MouseEvent) => {beat: number, note: number}
  // midi note when (x, y) lands on the keyboard, else undefined
  keyboardNoteAt: (x: number, y: number) => number?
}) ->
  {player, container, deferCleanup, screenPositionToBeatNote, keyboardNoteAt} := opts

  interaction := new Sprite
  interaction.width = 0x1000000
  interaction.height = 0x1000000
  interaction.interactive = true
  container.addChild interaction

  let toolHeld: Tool?
  heldKey .= -1

  interaction.on "pointerdown", ({data}) ->
    return if player.paymentRequired()

    {global, originalEvent} := data
    {x, y} := global
    {which, pointerId} := originalEvent

    if pointerId > 0
      document.body.setPointerCapture? pointerId

    key := keyboardNoteAt x, y
    if key?
      heldKey = key
      player.playNote player.activeChannel(), heldKey
      return

    // Performing is play-only: the keyboard still previews, but the note
    // field doesn't edit
    return unless player.playMode() is "edit"

    // Middle click pans the staff; there is nothing to pan here
    return if which is 2

    beatNote := screenPositionToBeatNote(x, y, originalEvent)

    if which is 3
      tool := player.activeTool()
      if tool.handlesRightClick
        toolHeld = tool
      else
        toolHeld = player.tools.erase
    else
      toolHeld = player.activeTool()

    toolHeld.down(player, beatNote, originalEvent)

  interaction.on "pointermove", ({data}) ->
    {global, originalEvent} := data
    {x, y} := global

    {beat, note} .= screenPositionToBeatNote(x, y, originalEvent)
    beat = quantize(max(beat, 0), player.quantize())

    player.notePosition ```
      Time: ${player.song.timeAtBeat(beat).toFixed(2)}s
      Beat: ${(beat + 1).toFixed(2)}
      Note: ${noteName clamp note, 0, 127}
    ```

  pointerMoveHandler := (e: MouseEvent) ->
    if toolHeld
      {pageX: x, pageY: y} := e
      {top: vy} := player.viewportElement().getBoundingClientRect()
      toolHeld.move(player, screenPositionToBeatNote(x, y - vy, e), e)

  document.addEventListener "pointermove", pointerMoveHandler
  deferCleanup.push ->
    document.removeEventListener "pointermove", pointerMoveHandler

  pointerUpHandler := (e: MouseEvent) ->
    if heldKey >= 0
      player.releaseNote player.activeChannel(), heldKey
      heldKey = -1

    if toolHeld
      {pageX: x, pageY: y} := e
      {top: vy} := player.viewportElement().getBoundingClientRect()
      toolHeld.up(player, screenPositionToBeatNote(x, y - vy, e), e)
      toolHeld = undefined

  document.addEventListener "pointerup", pointerUpHandler
  deferCleanup.push ->
    document.removeEventListener "pointerup", pointerUpHandler

  return

export default rollInteraction
